import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, TextField, NativeSelect, Stack, Typography, styled, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useNavigate } from 'react-router';
import Iconify from '../../components/Iconify';
import CustomModal from '../../components/CustomModal';
import conObj from '../../utils/connection-assistant';
import useLocales from '../../hooks/useLocales';
import { useDispatch, useSelector } from '../../redux/store';
import { openAddMemberBankDialog } from '../../redux/slices/addMemberBankDialog';
import useAuth from '../../hooks/useAuth';
import useCountdown from '../../hooks/useCountdown';

const StyledInput = styled(TextField)(({ theme }) => ({
  color: '#111',
  fontWeight: 700,
  fontSize: 14,
  flexGrow: 1,
  width: '100%',
  marginTop: 8,
  '& input': {
    color: '#111',
    fontWeight: 600,
    '&::placeholder': {
      fontWeight: 300,
      color: 'gray',
    },
  },
}));

const StyledSelect = styled(NativeSelect)(({ theme }) => ({
  width: '100%',
  color: '#111',
  fontWeight: 600,
  padding: '4px 11px',
  mt: 8,
  border: '1px solid #CBA135',
  borderRadius: 8,
  '.MuiSvgIcon-root ': {
    fill: '#CBA135 !important',
  },
}));

const InputLabel = styled(Typography)(({ theme }) => ({
  color: '#111',
}));

const AddButton = styled(LoadingButton)(({ theme }) => ({
  width: '100%',
  marginBottom: 8,
  background: 'linear-gradient(to top, #FFD700, #B8860B)',
  color: '#000',
  fontWeight: 500,
  fontSize: '17px',
  marginTop: '20px',
}));

const RequiredField = () => <span style={{ color: 'red', marginRight: 5 }}>*</span>;

const InputError = ({ children }) => <span style={{ fontSize: '.75rem', color: 'red' }}>{children}</span>;

const getOtpFingerprint = () => {
  const existingFingerprint = localStorage.getItem('fingerprint');

  if (existingFingerprint) {
    return existingFingerprint;
  }

  const generatedFingerprint =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  localStorage.setItem('fingerprint', generatedFingerprint);

  return generatedFingerprint;
};

const initNewBank = {
  accountName: '',
  bank: '',
  accountNumber: '',
  otpUuid: '',
  otpCode: '',
  bankBranch: ''
};

const WithdrawFormDialog = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { userProfile } = useAuth();

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const { isAddMemberBankDialog } = useSelector((x) => x.addMemberBankDialog);

  const [bankList, setBankList] = useState([]);

  const [errors, setErrors] = useState([]);

  const [newBankAccountForm, setNewBankAccountForm] = useState(initNewBank);

  const [resendExpiry, setResendExpiry] = useState(null);

  const resendCountdown = useCountdown(resendExpiry);

  const navigate = useNavigate();

  const selectedBank = useMemo(
    () => bankList.find((f) => f.id === newBankAccountForm?.bank),
    [bankList, newBankAccountForm?.bank]
  );
  
  const isBankOTP = useMemo(() => selectedBank?.require_otp, [selectedBank]);

  useEffect(() => {
    if (userProfile?.full_name) {
      setNewBankAccountForm((prev) => ({
        ...prev,
        accountName: userProfile.full_name,
        accountNumber: (userProfile?.UserProfile?.no_hp || "").replace(/[^0-9]/g, '').replace(/^88/, "")
      }));
    }
  }, [userProfile, selectedBank]);

  const censoreUserNoHp = useMemo(() => {
    const raw = userProfile?.UserProfile?.no_hp?.replace(/[^0-9]/g, '') || '';
    if (raw.length <= 3) return raw;
    const last3 = raw.slice(-3);
    return `XXXXXXX${last3}`;
  }, [userProfile?.UserProfile?.no_hp]);

  useEffect(() => {
    if (isAddMemberBankDialog?.open) getBankList();
  }, [isAddMemberBankDialog?.open]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setNewBankAccountForm((prevState) => {
      const nextState = { ...prevState, [name]: value };

      if (name === 'bank') {
        nextState.otpUuid = '';
        nextState.otpCode = '';
      }

      return nextState;
    });
  };

  const onRequestOtp = async () => {
    try {
      setIsButtonDisabled(true);
      setResendExpiry(null);

      const fingerprint = getOtpFingerprint();
      const response = await conObj.post('otp_request.json', {
        no_hp: `${newBankAccountForm.accountNumber}`,
        fingerprint,
        purpose: 'addbankaccount',
      });

      const otpUuid = response?.data?.data?.data?.otp_uuid || '';

      if (response?.status === 200 && response?.data?.data?.status === 1 && otpUuid) {
        setNewBankAccountForm((prev) => ({
          ...prev,
          otpUuid,
          otpCode: '',
        }));
        setResendExpiry(new Date(Date.now() + 60 * 1000).toISOString());
      }
    } catch (err) {
      console.log(`Server Error: ${err.message}`);
      enqueueSnackbar(err?.message, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const resendOtp = async () => {
    await onRequestOtp();
  };

  const getBankList = () => {
    conObj
      .post('transactions/supported_banks.json')
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            const _bankList = res.data?.data?.data?.[0]?.Bank || [];
            setBankList(_bankList);
            if (_bankList?.length === 1) {
              handleChange({ target: { name: 'bank', value: _bankList[0]?.id || '' } });
            }
          }
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
        enqueueSnackbar(err?.message, { variant: 'error', autoHideDuration: 5000 });
      });
  };

  const addMemberBank = () => {
    if (isValid()) {
      if (!newBankAccountForm?.otpUuid && isBankOTP) {
        onRequestOtp();
        return;
      }

      setIsButtonDisabled(true);

      const requestData = {
        'member_bank-type': 'bank',
        'member_bank-bank_id': newBankAccountForm?.bank,
        'member_bank-account_name': newBankAccountForm?.accountName.trim(),
        'member_bank-account_number': newBankAccountForm?.accountNumber.trim(),
        'member_bank-bank_branch': newBankAccountForm?.bankBranch.trim(),
      };

      if (isBankOTP) {
        requestData.otp_uuid = newBankAccountForm?.otpUuid;
        requestData.otp_code = newBankAccountForm?.otpCode;
      }

      conObj
        .post('members/bank_add.json', requestData)
        .then((res) => {
          if (res.status === 200) {
            if (res.data?.data?.status === 1) {
              enqueueSnackbar(res.data?.data?.msg, { variant: 'success', autoHideDuration: 5000 });
              if (isAddMemberBankDialog?.onClose) isAddMemberBankDialog?.onClose();

              dispatch(openAddMemberBankDialog({ open: false, onClose: null }));
              setNewBankAccountForm({
                accountName: userProfile?.full_name || '',
                bank: '',
                accountNumber: '',
                otpUuid: '',
                otpCode: '',
              });
              setResendExpiry(null);
              setErrors({});
            }
          }
        })
        .catch((err) => {
          console.log(`Server Error: ${err.message}`);
          // enqueueSnackbar(err?.message, { variant: 'error', autoHideDuration: 5000 });
          setErrors((prev) => ({ ...prev, otpCode: err?.message }));
        })
        .finally(() => {
          setIsButtonDisabled(false);
        });
    }
  };

  const isValid = () => {
    let valid = true;

    const _errors = {};

    if (!newBankAccountForm.accountName.trim()) {
      valid = false;
      _errors.accountName = translate('x_is_required', { x: translate('account_name') });
    } else if (newBankAccountForm.accountName.trim().length < 5) {
      valid = false;
      _errors.accountName = translate('account_name_must_be_4');
    } else {
      _errors.accountName = '';
    }

    if (!newBankAccountForm.bank) {
      valid = false;
      _errors.bank = translate('please_select_x', { x: translate('bank_account') });
    } else {
      _errors.bank = '';
    }

    const accNumberLenght = newBankAccountForm.accountNumber.trim().length;

    if (!newBankAccountForm.accountNumber.trim()) {
      valid = false;
      _errors.accountNumber = translate('x_is_required', {
        x: translate('account_number'),
      });
    } else if (parseInt(selectedBank?.min_length, 10) > 0 || parseInt(selectedBank?.max_length, 10) > 0) {
      if (parseInt(selectedBank?.min_length, 10) > 0 && accNumberLenght < parseInt(selectedBank?.min_length, 10)) {
        valid = false;
        _errors.accountNumber = translate('minimum_length_is_x', { x: selectedBank?.min_length });
      } else if (
        parseInt(selectedBank?.max_length, 10) > 0 &&
        accNumberLenght > parseInt(selectedBank?.max_length, 10)
      ) {
        valid = false;
        _errors.accountNumber = translate('maximum_length_is_x', { x: selectedBank?.max_length });
      } else {
        _errors.accountNumber = '';
      }
    } else {
      _errors.accountNumber = '';
    }

    if (isBankOTP && newBankAccountForm?.otpUuid && !newBankAccountForm?.otpCode?.trim()) {
      valid = false;
      _errors.otpCode = translate('x_is_required', { x: translate('verification_code') });
    } else {
      _errors.otpCode = '';
    }

    setErrors(_errors);

    return valid;
  };

  return (
    <CustomModal
      maxWidth="sm"
      open={isAddMemberBankDialog?.open}
      onClose={() => {
        dispatch(openAddMemberBankDialog({ open: false, onClose: null }));
        setNewBankAccountForm({
          accountName: userProfile?.full_name || '',
          bank: '',
          accountNumber: '',
          otpUuid: '',
          otpCode: '',
        });
        setResendExpiry(null);
        setErrors({});
      }}
      title={
        <Typography sx={{ color: '#111' }} variant="h5">
          {translate('add_account')}
        </Typography>
      }
      PaperProps={{
        sx: {
          backgroundColor: '#fff',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {!userProfile?.is_kyc_verified ? (
          <Alert severity="error">
            {translate('kyc_not_verified')}.{' '}
            <Typography
              component={'span'}
              sx={{ textDecoration: 'underline', fontWeight: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}
              onClick={() => {
                navigate('/user/kyc');
                dispatch(openAddMemberBankDialog({ open: false, onClose: null }));
              }}
            >
              {translate('proceed')}
            </Typography>{' '}
          </Alert>
        ) : (
          <>
            {!newBankAccountForm?.otpUuid ? (
              <>
                <Box sx={{ mb: '10px' }}>
                  <Stack direction="row" alignItems={'center'}>
                    <InputLabel>
                      <RequiredField />
                      {translate('bank')}
                    </InputLabel>
                  </Stack>

                  <StyledSelect
                    name="bank"
                    value={newBankAccountForm?.bank}
                    onChange={(e) => handleChange(e)}
                    disableUnderline={true}
                  >
                    <option value="">-</option>
                    {bankList?.map((item, index) => (
                      <option key={index} value={item?.id}>
                        {item?.name}
                      </option>
                    ))}
                  </StyledSelect>
                  {errors?.bank && <InputError>{errors?.bank}</InputError>}
                </Box>

                <Box sx={{ mb: '10px' }}>
                  <Stack direction="row" alignItems={'center'}>
                    <InputLabel>
                      <RequiredField />
                      {translate('account_holder_name')}
                    </InputLabel>
                  </Stack>

                  <StyledInput
                    variant="outlined"
                    value={newBankAccountForm.accountName}
                    name="accountName"
                    disabled
                    placeholder={translate('name_of_the_account_holder')}
                  />
                  {errors?.accountName && <InputError>{errors?.accountName}</InputError>}
                </Box>

                <Box sx={{ mb: '10px' }}>
                  <Stack direction="row" alignItems={'center'}>
                    <InputLabel>
                      <RequiredField />
                      {translate('account_number')}
                    </InputLabel>
                  </Stack>

                  <StyledInput
                    variant="outlined"
                    value={newBankAccountForm?.accountNumber}
                    name="accountNumber"
                    onChange={(e) => handleChange(e)}
                    placeholder={translate('fill_in_bank_account')}
                    disabled={isBankOTP}
                  />
                  {errors?.accountNumber && <InputError>{errors?.accountNumber}</InputError>}
                </Box>

                <Box sx={{ mb: '10px' }}>
                  <Stack direction="row" alignItems={'center'}>
                    <InputLabel>
                      {/* <RequiredField /> */}
                      {translate('bank_branch')}
                    </InputLabel>
                  </Stack>

                  <StyledInput
                    variant="outlined"
                    value={newBankAccountForm?.bankBranch}
                    name="bankBranch"
                    onChange={(e) => handleChange(e)}
                    placeholder={translate('fill_in_bank_branch')}
                  />
                  {/* {errors?.accountNumber && <InputError>{errors?.accountNumber}</InputError>} */}
                </Box>
              </>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {translate('verification_code_sent', {
                    x: censoreUserNoHp,
                  })}
                </Alert>

                <Box sx={{ mb: '10px' }}>
                  <Stack direction="row" alignItems={'center'}>
                    <InputLabel>
                      <RequiredField />
                      {translate('verification_code')}
                    </InputLabel>
                  </Stack>

                  <StyledInput
                    variant="outlined"
                    value={newBankAccountForm?.otpCode}
                    name="otpCode"
                    onChange={(e) => handleChange(e)}
                    placeholder={translate('input_otp')}
                  />
                  {errors?.otpCode && <InputError>{errors?.otpCode}</InputError>}
                </Box>

                <Typography sx={{ fontSize: 14, color: '#111' }}>
                  {resendCountdown?.seconds !== '00' ? (
                    `${translate('resend_code_in')} ${resendCountdown?.seconds}`
                  ) : (
                    <Typography
                      component="span"
                      sx={{ textDecoration: 'underline', fontWeight: 600, fontSize: 'inherit', cursor: 'pointer' }}
                      onClick={resendOtp}
                    >
                      {translate('send_again')}
                    </Typography>
                  )}
                </Typography>
              </>
            )}

            <AddButton
              onClick={() => {
                addMemberBank();
              }}
              disabled={isButtonDisabled}
            >
              {(!newBankAccountForm?.otpUuid && isBankOTP ? translate('proceed') : translate('submit')).toUpperCase()}
            </AddButton>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default WithdrawFormDialog;
